import { Menu } from '@tinycld/core/ui/menu'
import { useCallback, useMemo } from 'react'
import type * as Y from 'yjs'
import { useClipboard } from '../../hooks/use-clipboard'
import { useFilterView } from '../../hooks/use-filter-view'
import { useGridStore, useGridStoreApi } from '../../hooks/use-grid-store'
import { useSheetConditionalFormats } from '../../hooks/use-sheet-conditional-formats'
import { useYSheets } from '../../hooks/use-y-sheets'
import { rangeToSheetRelativeA1 } from '../../lib/conditional-format/a1'
import { anyRuleOverlapsRect } from '../../lib/conditional-format/range-index'
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from '../../lib/dimensions'
import { clearFilter } from '../../lib/filter'
import { pluralize } from '../../lib/pluralize'
import { allRanges, isDisjoint, primaryRange } from '../../lib/selection-range'
import { detectHeaderRow, sortRange } from '../../lib/sort'
import { useConditionalFormatPanelStore } from '../../lib/stores/conditional-format-panel-store'
import { columnLabel } from '../../lib/workbook-types'
import { MIN_COLS, MIN_ROWS } from './constants'
import { autosizeCol, commitColWidth, commitRowHeight } from './resize-actions'

interface HeaderContextMenuProps {
    doc: Y.Doc | null
    sheetId: string
}

// Right-click menu on the column-label or row-label cells. Distinct
// from CellContextMenu (right-click inside the grid body) and from
// HandleContextMenu (right-click on the resize handle between
// headers). openHeaderMenu in the store pre-selects the clicked
// row/col when it isn't already part of the active selection, so the
// menu's range-targeted actions operate on what the user expects.
export function HeaderContextMenu({ doc, sheetId }: HeaderContextMenuProps) {
    const target = useGridStore(s => s.headerMenu)
    const selection = useGridStore(s => s.selection)
    const disjoint = useGridStore(s => isDisjoint(s.selection))
    const store = useGridStoreApi()
    const onClose = useCallback(() => store.getState().closeHeaderMenu(), [store])

    const isOpen = target != null
    const anchor = useMemo(
        () => (target ? { x: target.cursor.x, y: target.cursor.y } : undefined),
        [target]
    )

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) onClose()
        },
        [onClose]
    )

    const sheets = useYSheets(doc)
    const sheet = sheets.find(s => s.id === sheetId)
    const rowCount = sheet?.rowCount ?? 0
    const colCount = sheet?.colCount ?? 0
    const displayedRowCount = Math.max(rowCount, MIN_ROWS)
    const displayedColCount = Math.max(colCount, MIN_COLS)
    const frozenRows = sheet?.frozenRows ?? 0
    const frozenCols = sheet?.frozenCols ?? 0
    const hasFreeze = frozenRows > 0 || frozenCols > 0

    const range = primaryRange(selection)
    const rowSpan = range != null ? range.endRow - range.startRow + 1 : 1
    const colSpan = range != null ? range.endCol - range.startCol + 1 : 1

    const isCol = target?.axis === 'col'

    const onInsertAbove = useCallback(
        () => store.getState().insertRowsAtSelection('above', displayedRowCount),
        [store, displayedRowCount]
    )
    const onInsertBelow = useCallback(
        () => store.getState().insertRowsAtSelection('below', displayedRowCount),
        [store, displayedRowCount]
    )
    const onInsertLeft = useCallback(
        () => store.getState().insertColumnsAtSelection('left', displayedColCount),
        [store, displayedColCount]
    )
    const onInsertRight = useCallback(
        () => store.getState().insertColumnsAtSelection('right', displayedColCount),
        [store, displayedColCount]
    )
    const onDeleteRows = useCallback(
        () => store.getState().deleteSelectedRows(rowCount),
        [store, rowCount]
    )
    const onDeleteCols = useCallback(
        () => store.getState().deleteSelectedColumns(colCount),
        [store, colCount]
    )

    const onClear = useCallback(() => store.getState().clearSelection(), [store])

    const clipboard = useClipboard({ doc, sheetId, store })
    const onCut = useCallback(() => {
        void clipboard.cut()
    }, [clipboard])
    const onCopy = useCallback(() => {
        void clipboard.copy()
    }, [clipboard])
    const onPaste = useCallback(() => {
        void clipboard.paste('all')
    }, [clipboard])

    // Freeze "up to" labels match the clicked header index — clearer
    // than the cell-menu's "up to row/col X (selection edge)" wording
    // since the user pointed at this specific header.
    const clickedIndex = target?.index ?? null
    const onFreezeRowsHere = useCallback(() => {
        if (clickedIndex == null) return
        store.getState().setFrozenRows(clickedIndex)
    }, [store, clickedIndex])
    const onFreezeColsHere = useCallback(() => {
        if (clickedIndex == null) return
        store.getState().setFrozenCols(clickedIndex)
    }, [store, clickedIndex])
    const onUnfreeze = useCallback(() => store.getState().unfreeze(), [store])

    const onAutosizeCol = useCallback(() => {
        if (clickedIndex == null) return
        autosizeCol(doc, sheetId, clickedIndex)
    }, [doc, sheetId, clickedIndex])
    const onResetColWidth = useCallback(() => {
        if (clickedIndex == null) return
        commitColWidth(doc, sheetId, clickedIndex, DEFAULT_COL_WIDTH)
    }, [doc, sheetId, clickedIndex])
    const onResetRowHeight = useCallback(() => {
        if (clickedIndex == null) return
        commitRowHeight(doc, sheetId, clickedIndex, DEFAULT_ROW_HEIGHT)
    }, [doc, sheetId, clickedIndex])

    // Sort/filter only make sense on a single contiguous column
    // selection — hide when disjoint. The cell menu does the same.
    const filterView = useFilterView(doc, sheetId)

    // Column-header sort uses the clicked column as the sort key but
    // must reorder every row across every column of the sheet — every
    // sibling column rides along with its row so a "Sort sheet A→Z"
    // does not desynchronise the table. The range spans all rows and
    // all columns; the sort key column is `range.startCol`.
    const fullSheetRange = useCallback(() => {
        if (range == null) return null
        const lastRow = Math.max(rowCount, displayedRowCount)
        const lastCol = Math.max(colCount, displayedColCount)
        return {
            startRow: 1,
            endRow: lastRow,
            startCol: 1,
            endCol: lastCol,
        }
    }, [range, rowCount, displayedRowCount, colCount, displayedColCount])

    const onSortAsc = useCallback(() => {
        if (doc == null || range == null) return
        const r = fullSheetRange()
        if (r == null) return
        const hasHeader = detectHeaderRow(doc, sheetId, r)
        const result = sortRange(doc, sheetId, r, range.startCol, 'asc', hasHeader)
        if (result.ok && result.mergesBroken > 0) {
            store.getState().setSortStatus({ mergesBroken: result.mergesBroken })
        }
    }, [doc, sheetId, fullSheetRange, range, store])

    const onSortDesc = useCallback(() => {
        if (doc == null || range == null) return
        const r = fullSheetRange()
        if (r == null) return
        const hasHeader = detectHeaderRow(doc, sheetId, r)
        const result = sortRange(doc, sheetId, r, range.startCol, 'desc', hasHeader)
        if (result.ok && result.mergesBroken > 0) {
            store.getState().setSortStatus({ mergesBroken: result.mergesBroken })
        }
    }, [doc, sheetId, fullSheetRange, range, store])

    const onOpenFilterDialog = useCallback(() => {
        if (clickedIndex == null) return
        store.getState().openFilterDialog(clickedIndex)
        store.getState().closeHeaderMenu()
    }, [store, clickedIndex])

    const sheetRules = useSheetConditionalFormats(doc, sheetId)
    const hasRuleOnSelection =
        range != null &&
        anyRuleOverlapsRect(sheetRules, {
            startRow: range.startRow,
            startCol: range.startCol,
            endRow: range.endRow,
            endCol: range.endCol,
        })

    const onOpenConditionalFormatting = useCallback(() => {
        const defaultRanges = allRanges(selection).map(r =>
            rangeToSheetRelativeA1(r.startRow, r.startCol, r.endRow, r.endCol)
        )
        useConditionalFormatPanelStore.getState().open(sheetId, { defaultRanges })
        onClose()
    }, [selection, sheetId, onClose])

    const conditionalFormattingMenuLabel = hasRuleOnSelection
        ? 'Edit conditional formatting…'
        : 'Conditional formatting…'

    const onRemoveFilter = useCallback(() => {
        if (doc == null) return
        clearFilter(doc, sheetId)
    }, [doc, sheetId])

    const filterMode = filterView?.mode ?? null
    const clickedHasFilter =
        filterView?.mode === 'header' && filterView.criteria[clickedIndex ?? -1] != null

    if (isCol) {
        return (
            <Menu
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                anchor={anchor}
                presentation="popover"
            >
                <Menu.Item label="Cut" onSelect={onCut} />
                <Menu.Item label="Copy" onSelect={onCopy} />
                <Menu.Item label="Paste" onSelect={onPaste} />
                <Menu.Separator />
                <Menu.Item
                    label={`Insert ${pluralize(colSpan, 'column')} left`}
                    onSelect={onInsertLeft}
                />
                <Menu.Item
                    label={`Insert ${pluralize(colSpan, 'column')} right`}
                    onSelect={onInsertRight}
                />
                <Menu.Item
                    label={colSpan === 1 ? 'Delete column' : `Delete ${colSpan} columns`}
                    onSelect={onDeleteCols}
                    isDisabled={colCount <= 1}
                />
                <Menu.Separator />
                <Menu.Item label="Clear contents" onSelect={onClear} />
                <Menu.Separator />
                <Menu.Item label="Sort sheet A→Z" onSelect={onSortAsc} isDisabled={disjoint} />
                <Menu.Item label="Sort sheet Z→A" onSelect={onSortDesc} isDisabled={disjoint} />
                <ColumnFilterItems
                    filterMode={filterMode}
                    clickedHasFilter={clickedHasFilter}
                    isDisabled={disjoint || clickedIndex == null}
                    onOpenFilterDialog={onOpenFilterDialog}
                    onRemoveFilter={onRemoveFilter}
                />
                <Menu.Separator />
                <FreezeHereItem
                    label={`Freeze up to column ${columnLabel(clickedIndex ?? 0)}`}
                    index={clickedIndex}
                    onSelect={onFreezeColsHere}
                />
                <Menu.Item label="Unfreeze" onSelect={onUnfreeze} isDisabled={!hasFreeze} />
                <Menu.Separator />
                <Menu.Item label="Auto-fit column width" onSelect={onAutosizeCol} />
                <Menu.Item label="Reset to default width" onSelect={onResetColWidth} />
                <Menu.Separator />
                <Menu.Item
                    label={conditionalFormattingMenuLabel}
                    onSelect={onOpenConditionalFormatting}
                />
            </Menu>
        )
    }

    return (
        <Menu
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            anchor={anchor}
            presentation="popover"
        >
            <Menu.Item label="Cut" onSelect={onCut} />
            <Menu.Item label="Copy" onSelect={onCopy} />
            <Menu.Item label="Paste" onSelect={onPaste} />
            <Menu.Separator />
            <Menu.Item
                label={`Insert ${pluralize(rowSpan, 'row')} above`}
                onSelect={onInsertAbove}
            />
            <Menu.Item
                label={`Insert ${pluralize(rowSpan, 'row')} below`}
                onSelect={onInsertBelow}
            />
            <Menu.Item
                label={rowSpan === 1 ? 'Delete row' : `Delete ${rowSpan} rows`}
                onSelect={onDeleteRows}
                isDisabled={rowCount <= 1}
            />
            <Menu.Separator />
            <Menu.Item label="Clear contents" onSelect={onClear} />
            <Menu.Separator />
            <FreezeHereItem
                label={`Freeze up to row ${clickedIndex}`}
                index={clickedIndex}
                onSelect={onFreezeRowsHere}
            />
            <Menu.Item label="Unfreeze" onSelect={onUnfreeze} isDisabled={!hasFreeze} />
            <Menu.Separator />
            <Menu.Item label="Reset to default height" onSelect={onResetRowHeight} />
            <Menu.Separator />
            <Menu.Item
                label={conditionalFormattingMenuLabel}
                onSelect={onOpenConditionalFormatting}
            />
        </Menu>
    )
}

function FreezeHereItem({
    label,
    index,
    onSelect,
}: {
    label: string
    index: number | null
    onSelect: () => void
}) {
    if (index == null || index <= 0) return null
    return <Menu.Item label={label} onSelect={onSelect} />
}

// No filter: offer the create dialog. A header-mode filter: edit or add
// the clicked column's criterion, plus remove-all. A range filter is
// managed from the cell menu, so the header menu shows nothing.
function ColumnFilterItems({
    filterMode,
    clickedHasFilter,
    isDisabled,
    onOpenFilterDialog,
    onRemoveFilter,
}: {
    filterMode: 'range' | 'header' | null
    clickedHasFilter: boolean
    isDisabled: boolean
    onOpenFilterDialog: () => void
    onRemoveFilter: () => void
}) {
    if (filterMode == null) {
        return (
            <Menu.Item
                label="Create filter…"
                onSelect={onOpenFilterDialog}
                isDisabled={isDisabled}
            />
        )
    }
    if (filterMode === 'header') {
        return (
            <>
                <Menu.Item
                    label={clickedHasFilter ? 'Edit filter…' : 'Add filter…'}
                    onSelect={onOpenFilterDialog}
                    isDisabled={isDisabled}
                />
                <Menu.Item label="Remove all filters" onSelect={onRemoveFilter} />
            </>
        )
    }
    return null
}
